import { Component, OnInit, Input } from '@angular/core';
import { AbstractAddComponent } from '../../../../../shared/abstracts/add/abstract-add/abstract-add.component';
import { IObservationDefinition, ICodeableConcept, IPatient, IAuditEvent, ICarePlan } from '@ahryman40k/ts-fhir-types/lib/R4';
import { ModalController, AlertController } from '@ionic/angular';
import { FormGroup, FormControl } from '@angular/forms';
import { TranslatePipe } from '../../../../../shared/translate/pipes/translate.pipe';
import { dataToAggregatedAuditEventDetail } from '../../../../functions/observation-definition.function';
import { Category } from '../../../../models/category.model';
import { AggregatedAlertDefaultValues } from '../../../../../shared/models/aggregated-alert-default-values.model';

@Component({
  selector: 'inc-observation-definition-add-aggregated',
  templateUrl: './observation-definition-add-aggregated.component.html',
  styleUrls: ['./observation-definition-add-aggregated.component.scss'],
})
export class ObservationDefinitionAddAggregatedComponent extends AbstractAddComponent
<{ data: IObservationDefinition, audit: IAuditEvent }[]> implements OnInit {
  @Input() isModal: boolean;
  @Input() patient: IPatient;
  @Input() user: any;
  @Input() careplan: ICarePlan;
  @Input() category: Category;
  @Input() timeUnits: any[] = [];
  @Input() operations: any[] = [] ;
  @Input() alerts: any[] = [];
  @Input() defaultValues: AggregatedAlertDefaultValues;

  careplanCode: ICodeableConcept;
  parameterCodes: ICodeableConcept[] = [];
  allSelect: boolean;
  editId: IObservationDefinition['id'];
  index = 0;

  constructor(private modalController: ModalController, private alertController: AlertController, private translatePipe: TranslatePipe) {
    super();
  }

  ngOnInit() {
    this.setCategoryValues();
    this.addAllSelect();
    this.setFormValue();
  }

  setCategoryValues() {
    if (this.category) {
      this.careplanCode = this.category.carePlan.value;
      this.allSelect = this.category.carePlan.allSelect ? this.category.carePlan.allSelect.aggregated : false;

      Object.values(this.category.parameters).map(value => {
        this.parameterCodes.push(value.value);
      });
    }
  }

  addAllSelect() {
    if (this.allSelect && !this.inData) {
      this.parameterCodes.unshift({ text: 'ALL' });
    }
  }

  setFormValue() {
    if (this.inData && this.inData[0] && this.inData[0].data) {
      this.convertToForm(this.inData[0].data);
    } else {
      this.form.patchValue({
        name: this.parameterCodes ? this.parameterCodes[this.defaultValues.name.position] : '',
        time: this.defaultValues ? this.defaultValues.time.value : 0,
        timeUnit: this.timeUnits ? this.timeUnits[this.defaultValues.time.position].value : '',
        operation: this.operations ? this.operations[this.defaultValues.operation.position].value : '',
        value: this.defaultValues ? this.defaultValues.operation.value: 0,
        alert: this.alerts ? this.alerts[this.defaultValues.alert.position].value : ''
      });
    }
  }

  resetForm() {
    return new FormGroup({
      name: new FormControl(''),
      time: new FormControl(''),
      timeUnit: new FormControl(''),
      operation: new FormControl(''),
      value: new FormControl(''),
      alert: new FormControl(''),
      comment: new FormControl('')
    });
  }

  close(data?: { data: IObservationDefinition, audit: IAuditEvent }[]) {
    if (this.isModal) {
      data ? this.modalController.dismiss(data) : this.modalController.dismiss();
    } else {
      this.save(data);
    }
  }

  convertToIObservationDef(data: any, index: number): IObservationDefinition {
    const aggregated: IObservationDefinition = {
      id: this.editId,
      meta: {
        lastUpdated: new Date().toISOString()
      },
      code: this.careplanCode,
      method: {
        text: 'aggregated'
      },
      qualifiedInterval: [{
        context: this.parameterCodes[index],
        range: {
          [data.alert === 'min' ? 'low' : 'high']: {
            value: data.value,
            unit: data.operation,
            system: 'http://unitsofmeasure.org',
            code: '{' + data.operation + '}'
          }
        }
      }, {
        context: this.parameterCodes[index],
        range: {
          [data.alert === 'min' ? 'low' : 'high']: {
            value: data.time,
            unit: data.timeUnit,
            system: 'http://unitsofmeasure.org',
            code: data.timeUnit
          }
        }
      }],
      text: {
        div: data.comment
      },
      resourceType: 'ObservationDefinition'
    };
    return aggregated;
  }

  add(data: any) {
    const allData: { data: IObservationDefinition, audit: IAuditEvent }[] = [];
    let convertedData: IObservationDefinition;

    if (this.allSelect && this.index === 0 && !this.inData) {
      for (let i = 1; i < this.parameterCodes.length; i++) {
        convertedData = this.convertToIObservationDef(data, i);
        allData.push({ data: convertedData, audit: this.createAuditEvent(convertedData) });
      }
    } else {
      convertedData = this.convertToIObservationDef(data, this.index);
      allData.push({ data: convertedData, audit: this.createAuditEvent(convertedData) });
    }
    this.close(allData);
  }

  selectChange(event: { detail: { value: ICodeableConcept } }) {
    this.index = this.parameterCodes.indexOf(event.detail.value);
  }

  convertToForm(data: IObservationDefinition) {
    if (data.method && data.method.text === 'aggregated' && data.qualifiedInterval) {
      this.editId = data.id;
      let values: any;
      if (data.qualifiedInterval[0].range.low && data.qualifiedInterval[1].range.low) {
        values = {
          name: '',
          time: data.qualifiedInterval[1].range.low.value,
          timeUnit: data.qualifiedInterval[1].range.low.unit,
          operation: data.qualifiedInterval[0].range.low.unit,
          value: data.qualifiedInterval[0].range.low.value,
          alert: 'min',
          comment: data.text && data.text.div ? data.text.div : ''
        };
      } else {
        values = {
          name: '',
          time: data.qualifiedInterval[1].range.high.value,
          timeUnit: data.qualifiedInterval[1].range.high.unit,
          operation: data.qualifiedInterval[0].range.high.unit,
          value: data.qualifiedInterval[0].range.high.value,
          alert: 'max',
          comment: data.text && data.text.div ? data.text.div : ''
        };
      }
      this.form.patchValue(values);
      this.form.patchValue({
        name: this.parameterCodes.filter(value => value.text === this.inData[0].data.qualifiedInterval[0].context.text)[0]
      });
      this.index = this.parameterCodes.indexOf(this.form.value.name);
    }
  }

  async openAlert(data?: any) {
    const alert = await this.alertController.create({
      header: this.translatePipe.transform('EDIT_ALERT'),
      message: this.translatePipe.transform('THERAPY_CHANGE_Q'),
      buttons: [{
        text: this.translatePipe.transform('CANCEL_L'),
        role: 'cancel'
      }, {
        text: this.translatePipe.transform('YES'),
        handler: () => { this.add(data); }
      }]
    });
    await alert.present();
  }

  createAuditEvent(data: IObservationDefinition): IAuditEvent {
    const newAudit: IAuditEvent = {
      id: null,
      agent: [{
        name: this.patient.name[0].text,
        requestor: false,
        who: {
          reference: this.patient.id
        }
      }],
      entity: [{
        description: data.text ? data.text.div : '',
        detail: dataToAggregatedAuditEventDetail(this.inData && this.inData[0] && this.inData[0].data ? this.inData[0].data : null, data),
        what: {
          reference: this.careplan.id
        }
      }],
      recorded: new Date().toISOString(),
      source: {
        observer: {
          display: this.user.displayName,
          id: this.user.uid
        }
      },
      type: {
        display: 'aggregated'
      },
      resourceType: 'AuditEvent'
    };

    return newAudit;
  }
}
